import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Send, RotateCcw, FileText, Printer } from "lucide-react";
import ProgressoJornada from "../components/ProgressoJornada";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Detecta e extrai alternativas no padrão "a) ... b) ... c) ..."
function parseMessage(content) {
  if (!content) return { text: content, options: null };
  const lines = content.split("\n");
  const optionRegex = /^([a-e])\)\s+(.+)/;
  const options = [];
  let firstOptionIndex = -1;

  lines.forEach((line, i) => {
    const match = line.trim().match(optionRegex);
    if (match) {
      if (firstOptionIndex === -1) firstOptionIndex = i;
      options.push({ letter: match[1], text: match[2] });
    }
  });

  if (options.length >= 2) {
    const text = lines.slice(0, firstOptionIndex).join("\n").trim();
    return { text, options };
  }
  return { text: content, options: null };
}

// Separa a fala da IA em "comentário" (o que ela observa/reconhece) e
// "pergunta" (a última frase, se terminar em "?") — pra colorir e isolar
// visualmente a pergunta numa linha própria, nunca grudada no comentário.
function splitCommentAndQuestion(text) {
  if (!text) return { comment: "", question: "" };
  const trimmed = text.trim();
  const sentences = trimmed.split(/(?<=[.!?])\s+/).filter(Boolean);
  if (sentences.length === 0) return { comment: trimmed, question: "" };
  const last = sentences[sentences.length - 1];
  if (/\?\s*$/.test(last)) {
    return { comment: sentences.slice(0, -1).join(" ").trim(), question: last.trim() };
  }
  return { comment: trimmed, question: "" };
}

export default function ChatPage() {
  const { moduleId } = useParams();
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [moduleName, setModuleName] = useState("");
  const [initializing, setInitializing] = useState(true);
  const [answeredMessages, setAnsweredMessages] = useState({});
  const [showPlanoButton, setShowPlanoButton] = useState(false);
  const [generatingPlano, setGeneratingPlano] = useState(false);
  const [plano, setPlano] = useState(null);
  const messagesContainerRef = useRef(null);
  const inputRef = useRef(null);
  const sidRef = useRef(localStorage.getItem("lumis_session_id"));

  useEffect(() => {
    const ensureSession = async () => {
      if (!sidRef.current) {
        try {
          const res = await axios.post(`${API}/session/create`);
          sidRef.current = res.data.session_id;
          localStorage.setItem("lumis_session_id", res.data.session_id);
        } catch (e) { console.error(e); }
      }
    };
    ensureSession().then(() => init());
  }, [moduleId]);

  // Scroll confiável para iOS — usa scrollTop direto no container
  const scrollToBottom = (smooth = false) => {
    setTimeout(() => {
      const el = messagesContainerRef.current;
      if (el) {
        if (smooth) {
          el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
        } else {
          el.scrollTop = el.scrollHeight;
        }
      }
    }, 80);
  };

  useEffect(() => { scrollToBottom(); }, [messages]);

  // iOS keyboard / visualViewport handling — re-anchor ao abrir/fechar teclado
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    const onResize = () => scrollToBottom();
    vv.addEventListener("resize", onResize);
    vv.addEventListener("scroll", onResize);
    return () => {
      vv.removeEventListener("resize", onResize);
      vv.removeEventListener("scroll", onResize);
    };
  }, []);

  const init = async () => {
    setInitializing(true);
    try {
      const modsRes = await axios.get(`${API}/modules`);
      const mod = modsRes.data.modules.find(m => m.module_id === moduleId);
      const modName = mod?.name || "este módulo";
      setModuleName(modName);
      const histRes = await axios.get(`${API}/chat/history/${sidRef.current}/${moduleId}`);
      const hist = histRes.data.messages || [];
      if (hist.length === 0) {
        const userName = localStorage.getItem("lumis_user_name");
        const nameLine = userName ? `Oi, ${userName}!` : "Oi!";
        const isRelational = /amoroso|familiar|parentalidade|relacionamento/i.test(modName);
        const greetingBody = isRelational
          ? `Antes da gente começar, queria entender de verdade o que tá rolando contigo sobre ${modName}. Como envolve pessoas próximas, vou precisar conhecer um pouco de você e do outro lado — quem ele(a) é, como reage, exemplos concretos. Só assim consigo te trazer um caminho que funcione na prática.`
          : `Antes da gente começar, queria entender de verdade o que tá rolando contigo sobre ${modName}. Vou fazer algumas perguntas pra entender melhor a sua situação — sem pressa. Só assim consigo te trazer algo que realmente faça sentido pra você.`;
        const greeting = `✦ ${nameLine} Eu sou o Lumis.\n\n${greetingBody}\n\nO papo todo dura uns 15 a 20 minutos. Bora?`;
        setMessages([{
          role: "assistant",
          content: greeting,
          timestamp: new Date().toISOString(),
          isInitial: true
        }]);
      } else {
        setMessages(hist);
        // Restaurar botão do plano se a conversa já chegou em encerramento
        const lastAi = [...hist].reverse().find(m => m.role === "assistant");
        if (lastAi && /volte quando quiser|até a próxima|cuide-se|estou aqui sempre|até logo/i.test(lastAi.content)) setShowPlanoButton(true);
      }
    } catch (e) { console.error(e); }
    finally {
      setInitializing(false);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  };

  const handleOptionClick = (letter, msgIndex) => {
    if (answeredMessages[msgIndex] !== undefined || isLoading) return;
    setAnsweredMessages(prev => ({ ...prev, [msgIndex]: letter }));
    sendMessage(letter);
  };

  const sendMessage = async (overrideText) => {
    const text = overrideText || input.trim();
    if (!text || isLoading) return;
    if (!overrideText) setInput("");
    setMessages(prev => [...prev, { role: "user", content: text, timestamp: new Date().toISOString() }]);
    setIsLoading(true);
    setMessages(prev => [...prev, { role: "assistant", content: "", isTyping: true, timestamp: new Date().toISOString() }]);
    try {
      const res = await axios.post(`${API}/chat/message`, { session_id: sidRef.current, module_id: moduleId, content: text });
      setMessages(prev => {
        const filtered = prev.filter(m => !m.isTyping);
        return [...filtered, { role: "assistant", content: res.data.response, timestamp: new Date().toISOString() }];
      });
      const isEncerramento = res.data.phase === "encerramento" ||
        /volte quando quiser|até a próxima|cuide-se|estou aqui sempre|até logo/i.test(res.data.response);
      const hasPlanoMessage = /preparei um plano de ação exclusivo|clique no botão abaixo para ver/i.test(res.data.response);
      if (isEncerramento || hasPlanoMessage) setShowPlanoButton(true);
    } catch (e) {
      const detail = e?.response?.data?.detail || "Tive um problema aqui. Pode tentar de novo?";
      setMessages(prev => {
        const filtered = prev.filter(m => !m.isTyping);
        return [...filtered, { role: "assistant", content: detail, timestamp: new Date().toISOString() }];
      });
    } finally {
      setIsLoading(false);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  };

  const onKey = (e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); } };

  const gerarPlano = async () => {
    setGeneratingPlano(true);
    try {
      const res = await axios.post(`${API}/chat/plano`, { session_id: sidRef.current, module_id: moduleId });
      setPlano({ conteudo: res.data.conteudo, module_name: moduleName, created_at: new Date().toISOString() });
    } catch (e) {
      const msg = e?.response?.status === 422
        ? "Continue o diálogo com o Lumis para gerar seu plano."
        : e?.response?.status === 429
        ? "Muitas gerações. Tente novamente em 1 hora."
        : "Erro ao gerar o plano. Tente novamente.";
      alert(msg);
    } finally {
      setGeneratingPlano(false);
    }
  };

  const fmt = (ts) => ts ? new Date(ts).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" }) : "";
  const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric" }) : "";

  if (initializing) return (
    <div className="h-[100dvh] bg-[#EEF2FF] flex items-center justify-center">
      <div className="text-[#1E3A8A] text-3xl animate-pulse font-outfit">✦</div>
    </div>
  );

  if (plano) {
    const { conteudo, module_name, created_at } = plano;
    return (
      <div className="min-h-screen bg-[#EEF2FF] print:bg-white" data-testid="plano-viewer">
        <div className="print:hidden sticky top-0 z-10 bg-[#EEF2FF]/95 backdrop-blur-xl border-b border-gray-200 px-4 py-3 flex items-center justify-between gap-3">
          <button
            data-testid="plano-back-btn"
            onClick={() => setPlano(null)}
            className="p-2 rounded-full hover:bg-gray-100 transition-all text-gray-500 hover:text-gray-900"
          >
            <ArrowLeft size={20} />
          </button>
          <button
            data-testid="plano-print-btn"
            onClick={() => window.print()}
            className="flex items-center gap-2 px-4 py-2 bg-[#1E3A8A] hover:bg-[#172554] text-white rounded-xl font-manrope text-sm transition-colors"
          >
            <Printer size={15} /> Imprimir
          </button>
        </div>

        <div className="max-w-2xl mx-auto px-5 py-8 space-y-8 print:text-black">
          <div className="text-center space-y-1">
            <p className="text-[#1E3A8A] print:text-black font-outfit text-2xl">✦ Lumis</p>
            <p className="text-gray-500 print:text-black text-[17px] font-manrope">Plano de Ação · {module_name}</p>
            <p className="text-gray-400 print:text-black text-[15px] font-manrope">{fmtDate(created_at)}</p>
          </div>

          {conteudo.frase_abertura && (
            <p className="text-gray-900 print:text-black font-manrope italic text-center leading-relaxed">{conteudo.frase_abertura}</p>
          )}

          {conteudo.contexto_atual && (
            <section className="space-y-3">
              <h2 className="text-[#1E3A8A] print:text-black font-outfit font-semibold text-[17px] uppercase tracking-wide">Onde você está agora</h2>
              {conteudo.contexto_atual.split("\n\n").map((p, i) => (
                <p key={i} className="text-gray-700 print:text-black font-manrope text-[17px] leading-relaxed">{p}</p>
              ))}
            </section>
          )}

          {conteudo.insights?.length > 0 && (
            <section className="space-y-4">
              <h2 className="text-[#1E3A8A] print:text-black font-outfit font-semibold text-[17px] uppercase tracking-wide">O que emergiu na sua conversa</h2>
              {conteudo.insights.map((ins, i) => (
                <div key={i} className="border-b border-gray-200 print:border-gray-300 pb-3">
                  <p className="text-gray-900 print:text-black font-manrope font-semibold text-[17px]">{ins.titulo}</p>
                  <p className="text-gray-500 print:text-black font-manrope text-[17px] mt-1">{ins.descricao}</p>
                  {ins.o_que_a_pesquisa_diz && (
                    <p className="text-amber-700 print:text-black font-manrope text-[15px] italic mt-2">📖 {ins.o_que_a_pesquisa_diz}</p>
                  )}
                </div>
              ))}
            </section>
          )}

          {conteudo.perfil_identidade && (
            <section className="space-y-3">
              <h2 className="text-[#1E3A8A] print:text-black font-outfit font-semibold text-[17px] uppercase tracking-wide">Quem você é nesse padrão</h2>
              <div className="bg-white print:bg-white print:border print:border-gray-300 rounded-2xl p-4 space-y-3">
                {conteudo.perfil_identidade.arquetipo && (
                  <p className="text-[#1E3A8A] print:text-black font-outfit font-bold text-[20px] text-center">{conteudo.perfil_identidade.arquetipo}</p>
                )}
                {conteudo.perfil_identidade.voce_em_seguranca && (
                  <div>
                    <p className="text-emerald-700 print:text-black font-manrope font-semibold text-[15px]">Você em segurança</p>
                    <p className="text-gray-700 print:text-black font-manrope text-[17px]">{conteudo.perfil_identidade.voce_em_seguranca}</p>
                  </div>
                )}
                {conteudo.perfil_identidade.voce_ativado && (
                  <div>
                    <p className="text-[#7B1E3A] print:text-black font-manrope font-semibold text-[15px]">Quando o padrão é ativado</p>
                    <p className="text-gray-700 print:text-black font-manrope text-[17px]">{conteudo.perfil_identidade.voce_ativado}</p>
                  </div>
                )}
                {conteudo.perfil_identidade.ciclo_que_se_repete?.length > 0 && (
                  <div>
                    <p className="text-gray-900 print:text-black font-manrope font-semibold text-[15px] mb-1">O ciclo que se repete</p>
                    {conteudo.perfil_identidade.ciclo_que_se_repete.map((passo, i) => (
                      <div key={i}>
                        <p className="text-gray-700 print:text-black font-manrope text-[15px] bg-gray-50 print:bg-white rounded-xl px-3 py-2">{passo}</p>
                        {i < conteudo.perfil_identidade.ciclo_que_se_repete.length - 1 && (
                          <p className="text-center text-gray-400 print:text-black text-[13px]">↓</p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                {conteudo.perfil_identidade.crenca_automatica && (
                  <p className="text-gray-700 print:text-black font-manrope text-[17px] italic">"{conteudo.perfil_identidade.crenca_automatica}"</p>
                )}
                {conteudo.perfil_identidade.ponto_cego && (
                  <div className="bg-[#DBEAFE] print:bg-white print:border print:border-gray-300 rounded-xl p-3">
                    <p className="text-[#1E3A8A] print:text-black font-manrope font-semibold text-[15px]">Seu ponto cego provável</p>
                    <p className="text-[#1E3A8A] print:text-black font-manrope text-[15px]">{conteudo.perfil_identidade.ponto_cego}</p>
                  </div>
                )}
              </div>
            </section>
          )}

          {conteudo.jornada_intro && (
            <section className="space-y-3">
              <h2 className="text-[#1E3A8A] print:text-black font-outfit font-semibold text-[17px] uppercase tracking-wide">Sua jornada de 4 semanas</h2>
              <p className="text-gray-700 print:text-black font-manrope text-[17px] leading-relaxed">{conteudo.jornada_intro}</p>
            </section>
          )}

          {conteudo.semanas?.length > 0 && (
            <section className="space-y-4">
              {conteudo.semanas.map((semana, i) => (
                <div key={i} className="bg-white print:bg-white print:border print:border-gray-300 rounded-2xl p-4 space-y-2">
                  <p className="text-gray-900 print:text-black font-manrope font-bold text-[17px]">{semana.titulo}</p>
                  {semana.objetivo && <p className="text-[#1E3A8A] print:text-black font-manrope text-[15px] italic">{semana.objetivo}</p>}
                  {semana.conteudo && semana.conteudo.split("\n\n").map((p, j) => (
                    <p key={j} className="text-gray-700 print:text-black font-manrope text-[17px]">{p}</p>
                  ))}
                  {semana.acoes?.map((acao, k) => (
                    <div key={k} className="pt-2 border-t border-gray-100 print:border-gray-300 space-y-1">
                      <p className="text-gray-900 print:text-black font-manrope font-semibold text-[15px]">{acao.nome}</p>
                      {acao.instrucao && <p className="text-gray-700 print:text-black font-manrope text-[17px]">{acao.instrucao}</p>}
                      {acao.fala_literal && (
                        <p className="text-[#1E3A8A] print:text-black font-manrope text-[17px] italic">💬 "{acao.fala_literal}"</p>
                      )}
                      {acao.gesto && <p className="text-gray-500 print:text-black font-manrope text-[15px]">Gesto: {acao.gesto}</p>}
                    </div>
                  ))}
                </div>
              ))}
            </section>
          )}

          {conteudo.tabela_progresso && (
            <ProgressoJornada
              instrucoes={conteudo.tabela_progresso.instrucoes}
              indicadores={conteudo.tabela_progresso.indicadores}
              faixasSignificado={conteudo.tabela_progresso.faixas_significado}
              storageKey={`lumis_progresso_${sidRef.current}_${moduleId}`}
            />
          )}

          {conteudo.frase_encerramento && (
            <p className="text-gray-900 print:text-black font-manrope italic text-center leading-relaxed">{conteudo.frase_encerramento}</p>
          )}

          <p className="text-gray-400 print:text-black text-center text-[15px] font-manrope pt-4 border-t border-gray-200 print:border-gray-300">
            Este material é de apoio ao autoconhecimento e não substitui acompanhamento psicológico ou médico profissional.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-[100dvh] bg-[#EEF2FF] flex flex-col max-w-md mx-auto" data-testid="chat-page">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-[#EEF2FF]/95 backdrop-blur-xl border-b border-gray-200 px-4 py-3 flex items-center gap-3">
        <button
          data-testid="chat-back-btn"
          onClick={() => navigate("/modules")}
          className="p-2 rounded-full hover:bg-gray-100 transition-all text-gray-500 hover:text-gray-900"
        >
          <ArrowLeft size={20} />
        </button>
        <div className="flex items-center gap-3 flex-1">
          <div className="w-9 h-9 rounded-full bg-[#DBEAFE] border border-[#1E3A8A]/20 flex items-center justify-center text-[#1E3A8A] text-base font-outfit shrink-0">
            ✦
          </div>
          <div>
            <h2 className="text-gray-900 text-sm font-outfit font-medium">Lumis</h2>
            <p className="text-gray-500 text-xs font-manrope leading-none mt-0.5">{moduleName}</p>
          </div>
        </div>
        <button
          data-testid="chat-reset-btn"
          onClick={async () => {
            if (!window.confirm("Reiniciar esta conversa? O histórico atual deste módulo será apagado.")) return;
            try {
              await axios.delete(`${API}/chat/history/${sidRef.current}/${moduleId}`);
              setMessages([]);
              setAnsweredMessages({});
              setShowPlanoButton(false);
              await init();
            } catch (e) { console.error(e); }
          }}
          className="flex items-center gap-1 px-2.5 py-1.5 rounded-full hover:bg-gray-100 transition-all text-gray-500 hover:text-gray-900 text-xs font-manrope"
          title="Reiniciar conversa"
        >
          <RotateCcw size={14} /> Refazer
        </button>
        <div className="w-2 h-2 rounded-full bg-emerald-400/70 shrink-0" />
      </div>

      {/* Messages */}
      <div ref={messagesContainerRef} className="flex-1 overflow-y-auto" data-testid="chat-messages">
        <div className="min-h-full flex flex-col justify-end p-4 space-y-4">
          {messages.map((msg, i) => {
          const parsed = msg.role === "assistant" && !msg.isTyping ? parseMessage(msg.content) : null;
          const isAnswered = answeredMessages[i] !== undefined;
          const chosenLetter = answeredMessages[i];

          return (
            <div key={i} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"} lumis-fade`}>
              {msg.role === "assistant" && (
                <div className="w-7 h-7 rounded-full bg-white border border-[#1E3A8A]/15 flex items-center justify-center text-[#1E3A8A] text-xs mr-2 mt-1 shrink-0 font-outfit">
                  ✦
                </div>
              )}
              <div className={`max-w-[85%] ${
                msg.role === "user"
                  ? "rounded-2xl rounded-tr-sm bg-[#1E3A8A] text-white px-4 py-3"
                  : "rounded-2xl rounded-tl-sm bg-white text-gray-800 border border-gray-200 px-4 py-3"
              }`}>
                {msg.isTyping ? (
                  <div className="flex gap-1.5 py-1">
                    {[0, 1, 2].map(j => (
                      <div key={j} className="w-1.5 h-1.5 bg-[#1E3A8A]/50 rounded-full animate-bounce" style={{ animationDelay: `${j * 0.15}s` }} />
                    ))}
                  </div>
                ) : (
                  <>
                    {msg.role === "assistant" ? (
                      (() => {
                        const { comment, question } = splitCommentAndQuestion(parsed.text);
                        return (
                          <>
                            {comment && (
                              <p className="text-sm font-manrope leading-relaxed whitespace-pre-line text-[#1E3A8A]">
                                {comment}
                              </p>
                            )}
                            {question && (
                              <p className={`text-sm font-manrope leading-relaxed whitespace-pre-line text-[#7B1E3A]${comment ? " mt-2" : ""}`}>
                                {question}
                              </p>
                            )}
                          </>
                        );
                      })()
                    ) : (
                      <p className="text-sm font-manrope leading-relaxed whitespace-pre-line">
                        {msg.content}
                      </p>
                    )}

                    {/* Alternativas clicáveis */}
                    {parsed?.options && (
                      <div className="mt-3 space-y-2" data-testid={`options-group-${i}`}>
                        {parsed.options.map(opt => {
                          const isChosen = isAnswered && chosenLetter === opt.letter;
                          const isDimmed = isAnswered && chosenLetter !== opt.letter;
                          return (
                            <button
                              key={opt.letter}
                              data-testid={`option-${opt.letter}-msg-${i}`}
                              onClick={() => handleOptionClick(opt.letter, i)}
                              disabled={isAnswered}
                              className={`w-full text-left rounded-xl px-3 py-2.5 text-sm font-manrope flex items-start gap-2 transition-all duration-200 ${
                                isChosen
                                  ? "bg-[#1E3A8A] text-white border border-[#1E3A8A]"
                                  : isDimmed
                                  ? "bg-gray-50 text-gray-400 border border-gray-200 cursor-not-allowed"
                                  : "bg-gray-100 text-gray-700 border border-gray-300 hover:bg-[#DBEAFE] hover:border-[#1E3A8A]/40 active:scale-[0.98] cursor-pointer"
                              }`}
                            >
                              <span className={`shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold mt-0.5 ${
                                isChosen ? "bg-gray-500 text-white" : isDimmed ? "bg-gray-100 text-gray-400" : "bg-[#1E3A8A]/20 text-[#1E3A8A]"
                              }`}>
                                {opt.letter}
                              </span>
                              <span className="leading-snug">{opt.text}</span>
                            </button>
                          );
                        })}
                      </div>
                    )}

                    {!msg.isInitial && (
                      <p className={`text-xs mt-2 ${msg.role === "user" ? "text-gray-400" : "text-gray-400"}`}>{fmt(msg.timestamp)}</p>
                    )}
                  </>
                )}
              </div>
            </div>
          );
        })}
        <div className="h-4" /> {/* scroll anchor */}
        </div>
      </div>

      {/* Botão do Plano de Ação */}
      {showPlanoButton && (
        <div className="px-4 pt-3 pb-1 bg-[#EEF2FF]">
          <button
            data-testid="plano-fechamento-btn"
            onClick={gerarPlano}
            disabled={generatingPlano}
            className="w-full flex items-center justify-center gap-2 bg-[#DBEAFE] border border-[#1E3A8A]/20 text-[#1E3A8A] rounded-2xl px-4 py-3 text-sm font-manrope hover:bg-[#1E3A8A]/20 active:scale-[0.98] transition-all disabled:opacity-50"
          >
            <FileText size={15} className={generatingPlano ? "animate-bounce" : ""} />
            {generatingPlano ? "Gerando seu plano..." : "✦ Ver meu Plano de Ação"}
          </button>
        </div>
      )}

      {/* Input */}
      <div className="bg-[#EEF2FF]/95 backdrop-blur-xl border-t border-gray-200 p-3 pb-[60px]" data-testid="chat-input-area">
        <div className="flex items-end gap-2">
          <textarea
            ref={inputRef}
            data-testid="chat-input"
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={onKey}
            onFocus={() => scrollToBottom()}
            placeholder="Escreva sua mensagem..."
            rows={1}
            style={{ lineHeight: "1.5" }}
            className="flex-1 bg-white text-gray-900 border border-gray-300 rounded-3xl px-5 py-3 focus:border-[#1E3A8A]/60 focus:ring-1 focus:ring-[#1E3A8A]/30 outline-none transition-all placeholder:text-gray-400 font-manrope text-sm resize-none max-h-28"
          />
          <button
            data-testid="chat-send-btn"
            onClick={() => sendMessage()}
            disabled={!input.trim() || isLoading}
            className="bg-[#1E3A8A] text-white rounded-full p-3 hover:bg-[#172554] active:scale-[0.95] transition-all disabled:opacity-30 shrink-0"
          >
            <Send size={18} />
          </button>
        </div>
      </div>
    </div>
  );
}
